import { ReactNode, useCallback } from 'react';
import {
    getDurationString,
    getTicketStatusColor,
    Identification,
    Ticket,
} from '../../model/ticket.types';
import { Box, Card, Chip } from '@mui/material';
import { TicketRefundBlock } from '../../ui/ticketDrawerContent/ticketRefundBlock';
import { TicketDrawerLabel } from '../../ui/ticketDrawerContent/ticketDrawerLabel';
import { TicketDrawerBlock } from '../../ui/ticketDrawerContent/ticketDrawerBlock';
import { getEnvironmentAsString } from '../../../environment/model/environment.types';
import { LiftOperator } from '../../../liftoperator/model/liftoperator.types';
import { getMoneyAsString } from '../../../money/lib/utils';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { formatDateToString } from '../../lib/ticketUiUtils';
import { InsuranceCard } from '../../ui/ticketDrawerContent/insuranceCard';
import { PermissionStatusBlock } from '../../ui/ticketDrawerContent/permissionStatusBlock';
import { useTranslation } from 'react-i18next';
import { useTicketLabelTranslations } from '../../lib/hooks/useTicketLabelTranslations';

// util functions

export const ticketContentBlockIds = [
    'orderReference',
    'firstname',
    'lastname',
    'email',
    'ticketOwnerData',
    'identifications',
    'environment',
    'status',
    'cancelableCondition',
    'cancelable',
    'cancellationDate',
    'cancelledBy',
    'cancellationAction',
    'orderDisplayId',
    'pickUpCode',
    'orderDate',
    'startDate',
    'liftOperator',
    'duration',
    'ticketName',
    'personType',
    'price',
    'bookingOption',
    'priceReason',
    'pdfUrl',
    'insurance',
    'ticketId',
    'permissionStatus',
] as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const identificationContentBlockIds = [
    'keycard.serialNumber',
    'keycard.external',
    'keycardOrderedSameTime',
    'downloadLink',
    'birthdate',
    'firstname',
    'lastname',
] as const;

export type TicketContentBlockId = (typeof ticketContentBlockIds)[number];

export type IdentificationContentBlockId =
    (typeof identificationContentBlockIds)[number];

type TicketContentBlock = {
    blockId: TicketContentBlockId;
    header: string;
    content: ReactNode;
};

type IdentificationContentBlock = {
    blockId: IdentificationContentBlockId;
    header: string;
    content: ReactNode;
};

export const useTicketDetailsBlocksFactory = () => {
    const { t, i18n } = useTranslation();
    const { getOrderStatusAsString } = useTicketLabelTranslations();

    return useCallback(
        ({
            ticket,
            identifications,
            liftOperator,
        }: {
            ticket: Ticket;
            identifications: IdentificationContentBlock[][];
            liftOperator: LiftOperator;
        }): TicketContentBlock[] => [
            {
                blockId: 'orderReference',
                header: t('ticketDetails.orderReference'),
                content: (
                    <TicketDrawerLabel>
                        {ticket.orderReference || '-'}
                    </TicketDrawerLabel>
                ),
            },
            {
                blockId: 'firstname',
                header: t('ticketDetails.firstname'),
                content: (
                    <TicketDrawerLabel>{ticket.firstname}</TicketDrawerLabel>
                ),
            },
            {
                blockId: 'lastname',
                header: t('ticketDetails.lastname'),
                content: (
                    <TicketDrawerLabel>{ticket.lastname}</TicketDrawerLabel>
                ),
            },
            {
                blockId: 'email',
                header: t('ticketDetails.emailBooker'),
                content: <TicketDrawerLabel>{ticket.email}</TicketDrawerLabel>,
            },
            {
                blockId: 'ticketOwnerData',
                header: t('ticketDetails.ticketBuyer'),
                content: (
                    <TicketDrawerLabel>
                        {ticket.lastname} {ticket.firstname}{' '}
                        {ticket.permissionNumber &&
                            `, (${ticket.permissionNumber})`}{' '}
                        {ticket.barcodeNumber && `, (${ticket.barcodeNumber})`}
                    </TicketDrawerLabel>
                ),
            },
            {
                blockId: 'orderDisplayId',
                header: t('ticketDetails.starjackOrderNumber'),
                content: (
                    <TicketDrawerLabel>
                        {ticket.orderDisplayId}
                    </TicketDrawerLabel>
                ),
            },
            {
                blockId: 'pickUpCode',
                header: t('ticketDetails.pickUpCode'),
                content: (
                    <TicketDrawerLabel>
                        {ticket.confirmationNumber || '-'}
                    </TicketDrawerLabel>
                ),
            },
            {
                blockId: 'orderDate',
                header: t('ticketDetails.orderDate'),
                content: (
                    <TicketDrawerLabel>
                        {formatDateToString(ticket.orderDate, {
                            detailed: true,
                        })}
                    </TicketDrawerLabel>
                ),
            },

            {
                blockId: 'startDate',
                header: t('ticketDetails.startDate'),
                content: (
                    <TicketDrawerLabel>
                        {formatDateToString(ticket.startDate)}
                    </TicketDrawerLabel>
                ),
            },

            {
                blockId: 'liftOperator',
                header: t('ticketDetails.liftOperator'),
                content: (
                    <TicketDrawerLabel>
                        {liftOperator?.name?.[i18n.language] || '-'}
                    </TicketDrawerLabel>
                ),
            },

            {
                blockId: 'duration',
                header: t('ticketDetails.duration'),
                content: (
                    <TicketDrawerLabel>
                        {getDurationString(ticket.duration, ticket.type)}
                    </TicketDrawerLabel>
                ),
            },

            {
                blockId: 'ticketName',
                header: t('ticketDetails.ticketName'),
                content: <TicketDrawerLabel>{ticket.name}</TicketDrawerLabel>,
            },

            {
                blockId: 'personType',
                header: t('ticketDetails.personType'),
                content: (
                    <TicketDrawerLabel>{ticket.personName}</TicketDrawerLabel>
                ),
            },

            {
                blockId: 'price',
                header: t('ticketDetails.price'),
                content: (
                    <TicketDrawerLabel>
                        {getMoneyAsString(ticket.price)}
                    </TicketDrawerLabel>
                ),
            },

            {
                blockId: 'bookingOption',
                header: t('ticketDetails.bookingOption'),
                content: (
                    <TicketDrawerLabel>
                        {ticket.identificationType}
                    </TicketDrawerLabel>
                ),
            },

            {
                blockId: 'priceReason',
                header: t('ticketDetails.priceReason'),
                content: (
                    <TicketDrawerLabel>
                        {ticket.priceReason || '-'}
                    </TicketDrawerLabel>
                ),
            },

            {
                blockId: 'ticketId',
                header: t('ticketDetails.ticketId'),
                content: (
                    <TicketDrawerLabel>{ticket.id || '-'}</TicketDrawerLabel>
                ),
            },

            {
                blockId: 'identifications',
                header: t('ticketDetails.people'),
                content: identifications.map((identificationBlocks, index) => (
                    <Card key={index}>
                        {identificationBlocks.map((identificationBlock) => (
                            <TicketDrawerBlock
                                header={identificationBlock.header}
                                key={identificationBlock.blockId}
                            >
                                {identificationBlock.content}
                            </TicketDrawerBlock>
                        ))}
                    </Card>
                )),
            },

            {
                blockId: 'insurance',
                header: t('insurance.header'),
                content: ticket.insurance ? (
                    <InsuranceCard ticket={ticket} />
                ) : (
                    <TicketDrawerLabel>
                        {t('insurance.notBooked')}
                    </TicketDrawerLabel>
                ),
            },

            {
                blockId: 'environment',
                header: t('ticketDetails.salesInterface'),
                content: (
                    <Chip
                        color='primary'
                        label={
                            <TicketDrawerLabel>
                                {getEnvironmentAsString(ticket.environment)}
                            </TicketDrawerLabel>
                        }
                    />
                ),
            },

            {
                blockId: 'pdfUrl',
                header: t('ticketDetails.pdfDownloadLink'),
                content: (
                    <>
                        {ticket.pdfUrl ? (
                            <Chip
                                color='primary'
                                onClick={() => {
                                    if (ticket.pdfUrl) {
                                        window.open(ticket.pdfUrl, '_blank');
                                    }
                                }}
                                label={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <DownloadForOfflineIcon fontSize='small' />
                                        <TicketDrawerLabel
                                            sx={{
                                                ml: 2,
                                            }}
                                        >
                                            {t('ticketDetails.pdfDownload')}
                                        </TicketDrawerLabel>
                                    </Box>
                                }
                            />
                        ) : (
                            <TicketDrawerLabel>
                                {t('ticketDetails.noPdfAvailable')}
                            </TicketDrawerLabel>
                        )}
                    </>
                ),
            },
            {
                blockId: 'permissionStatus',
                header: t('ticketDetails.permissionStatus'),
                content: <PermissionStatusBlock ticketId={ticket.id} />,
            },
            {
                blockId: 'status',
                header: t('ticketDetails.status'),
                content: (
                    <Chip
                        color={getTicketStatusColor(ticket.status)}
                        label={
                            <TicketDrawerLabel>
                                {getOrderStatusAsString(ticket.status)}
                            </TicketDrawerLabel>
                        }
                    />
                ),
            },
            {
                blockId: 'cancelableCondition',
                header: t('ticketDetails.cancelableCondition'),
                content: (
                    <Chip
                        label={
                            <TicketDrawerLabel>
                                {ticket.cancelableCondition
                                    ? t('ticketDetails.ruleCancelable')
                                    : t('ticketDetails.ruleNotCancelable')}
                            </TicketDrawerLabel>
                        }
                        color={ticket.cancelableCondition ? 'success' : 'error'}
                    />
                ),
            },
            {
                blockId: 'cancelable',
                header: t('ticketDetails.ticketCancelable'),
                content: (
                    <Chip
                        label={
                            <TicketDrawerLabel>
                                {ticket.cancelable
                                    ? t('ticketDetails.ticketCancelableYes')
                                    : t('ticketDetails.ticketCancelableNo')}
                            </TicketDrawerLabel>
                        }
                        color={ticket.cancelable ? 'success' : 'error'}
                    />
                ),
            },
            {
                blockId: 'cancellationDate',
                header: t('ticketDetails.refundCancel'),
                content: <TicketRefundBlock ticket={ticket} />,
            },
        ],
        [t, getOrderStatusAsString]
    );
};

export const useIdentificationBlocksFactory = () => {
    const { t } = useTranslation();

    return useCallback(
        (identifications: Identification): IdentificationContentBlock[] => [
            {
                blockId: 'firstname',
                header: t('identificationDetails.firstname'),
                content: (
                    <TicketDrawerLabel>
                        {identifications.firstname || '-'}
                    </TicketDrawerLabel>
                ),
            },

            {
                blockId: 'lastname',
                header: t('identificationDetails.lastname'),
                content: (
                    <TicketDrawerLabel>
                        {identifications.lastname || '-'}
                    </TicketDrawerLabel>
                ),
            },

            {
                blockId: 'keycard.serialNumber',
                header: t('identificationDetails.keycardSerial'),
                content: (
                    <TicketDrawerLabel>
                        {identifications.keycard?.serialNumber || '-'}
                    </TicketDrawerLabel>
                ),
            },
            {
                blockId: 'keycard.external',
                header: t('identificationDetails.keycardExternal'),
                content: (
                    <TicketDrawerLabel>
                        {identifications.keycard?.external || '-'}
                    </TicketDrawerLabel>
                ),
            },
            {
                blockId: 'keycardOrderedSameTime',
                header: t('identificationDetails.productionTime'),
                content: (
                    <TicketDrawerLabel>
                        {identifications.keycardOrderedSameTime
                            ? formatDateToString(identifications.productionDate)
                            : '-'}
                    </TicketDrawerLabel>
                ),
            },
            {
                blockId: 'birthdate',
                header: t('identificationDetails.birthdate'),
                content: (
                    <TicketDrawerLabel>
                        {formatDateToString(identifications.birthday)}
                    </TicketDrawerLabel>
                ),
            },
        ],
        [t]
    );
};
